import { Clearable } from '@euler/components/Clearable';
import {
  memo,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  NativeSyntheticEvent,
  Platform,
  StyleProp,
  TextInput,
  TextInputEndEditingEventData,
  TextInputFocusEventData,
  TextInputProps,
  TextStyle,
  ViewStyle,
} from 'react-native';

export type NumberInputFeedback = 'show-invalid-state' | 'reset';

export type NumberInputProps = Omit<TextInputProps, 'value'> & {
  value?: number;
  focusedStyle?: TextInputProps['style'];
  onRequestFeedback?: (type: NumberInputFeedback) => void;
  onChangeValue?: (value: number | undefined) => void;
  renderInput?: (props: TextInputProps) => ReactNode;
};

const validate = (s: string) =>
  /^\s*[+-]?\d+(\.(\d+)?)?\s*$/.test(s) || /^\s*$/.test(s);

const isPrefix = (s: string) => /^\s*[+-]$/.test(s);

const stringRepresentation = (value: number | undefined) =>
  value != null ? String(value) : '';

export const ClearableNumberInput = memo(
  ({
    style,
    inputStyle,
    clearStyle,
    clearButtonStyle,
    clearIconSize,
    clearIconColor,
    clearButtonMode,
    withNativeClearButton = true,
    onFocus,
    onBlur,
    ...props
  }: Omit<NumberInputProps, 'style'> & {
    style?: StyleProp<ViewStyle>;
    inputStyle?: StyleProp<TextStyle>;
    clearStyle?: StyleProp<ViewStyle>;
    clearButtonStyle?: StyleProp<ViewStyle>;
    clearIconSize?: number;
    clearIconColor?: string;
    withNativeClearButton?: boolean;
  }) => {
    const [focused, setFocused] = useState(false);

    const onClear = useCallback(() => {
      props.onChangeText?.('');
      props.onChangeValue?.(undefined);
    }, [props]);

    const showClearButton = Boolean(
      props.value &&
        (Platform.OS !== 'ios' || !withNativeClearButton) &&
        (clearButtonMode == 'always' ||
          (clearButtonMode == 'while-editing' && focused) ||
          (clearButtonMode == 'unless-editing' && !focused)),
    );

    const onInternalBlur = useCallback(
      (e: NativeSyntheticEvent<TextInputFocusEventData>) => {
        onBlur?.(e);
        setFocused(false);
      },
      [onBlur],
    );

    const onInternalFocus = useCallback(
      (e: NativeSyntheticEvent<TextInputFocusEventData>) => {
        onFocus?.(e);
        setFocused(true);
      },
      [onFocus],
    );

    return (
      <Clearable
        style={style}
        clearStyle={clearStyle}
        buttonStyle={clearButtonStyle}
        iconSize={clearIconSize}
        iconColor={clearIconColor}
        showClearButton={showClearButton}
        onClear={onClear}
      >
        <NumberInput
          {...props}
          style={inputStyle}
          onFocus={onInternalFocus}
          onBlur={onInternalBlur}
          clearButtonMode={
            Platform.OS === 'ios' && withNativeClearButton
              ? clearButtonMode
              : 'never'
          }
        />
      </Clearable>
    );
  },
);

export const NumberInput = memo(
  ({
    value,
    renderInput,
    style,
    focusedStyle,
    onFocus,
    onBlur,
    onChangeText,
    onEndEditing,
    onChangeValue,
    onRequestFeedback,
    ...props
  }: NumberInputProps) => {
    const [text, setText] = useState(stringRepresentation(value));
    const lastValue = useRef(text);
    const lastFeedback = useRef<NumberInputFeedback>();
    const isValid = useRef(validate(text));
    const [focused, setFocused] = useState(false);

    useEffect(() => {
      const str = stringRepresentation(value);
      if (lastValue.current !== str) {
        lastValue.current = str;
        setText(str);
      }
    }, [value]);

    const onInternalChangeText = useCallback(
      (s: string) => {
        if (!isPrefix(s)) {
          s = s.replace(/[^+\-0-9.]/g, '').replace(/\.{2,}/g, '.');
        }
        lastValue.current = s;
        setText(s);
        onChangeText?.(s);

        isValid.current = false;

        let feedback: NumberInputFeedback = 'show-invalid-state';
        if (validate(s)) {
          const v = /^\s*$/.test(s) ? undefined : Number(s);
          onChangeValue?.(v);
          feedback = 'reset';
          isValid.current = true;
        } else if (isPrefix(s)) {
          feedback = 'reset';
        }

        if (feedback !== lastFeedback.current) {
          lastFeedback.current = feedback;
          onRequestFeedback?.(feedback);
        }
      },
      [onChangeText, onChangeValue, onRequestFeedback],
    );

    const onInternalEndEditing = useCallback(
      (e: NativeSyntheticEvent<TextInputEndEditingEventData>) => {
        onEndEditing?.(e);
      },
      [onEndEditing],
    );

    const onInternalBlur = useCallback(
      (e: NativeSyntheticEvent<TextInputFocusEventData>) => {
        if (!isValid.current) {
          onRequestFeedback?.('show-invalid-state');
        }
        onBlur?.(e);
        setFocused(false);
      },
      [onBlur, onRequestFeedback],
    );

    const onInternalFocus = useCallback(
      (e: NativeSyntheticEvent<TextInputFocusEventData>) => {
        onRequestFeedback?.('reset');
        onFocus?.(e);
        setFocused(true);
      },
      [onFocus, onRequestFeedback],
    );

    if (renderInput != null) {
      return renderInput({
        numberOfLines: 1,
        ...props,
        style: [style, focused ? focusedStyle : undefined],
        value: text,
        onChangeText: onInternalChangeText,
        onFocus: onInternalFocus,
        onBlur: onInternalBlur,
        onEndEditing: onInternalEndEditing,
      });
    }

    return (
      <TextInput
        numberOfLines={1}
        style={[style, focused ? focusedStyle : undefined]}
        {...props}
        value={text}
        onChangeText={onInternalChangeText}
        onFocus={onInternalFocus}
        onBlur={onInternalBlur}
        onEndEditing={onInternalEndEditing}
      />
    );
  },
);
